import React, { useEffect, useState } from "react";
import { Loader2, Save, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchSiteSettings, updateSiteSettings } from "@/lib/api";
import { useSiteContent } from "@/lib/SiteContent";

const Section = ({ title, eyebrow, children }) => (
  <section className="glass-card neon-border p-6 md:p-8 rounded-[18px] border">
    <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
      <span className="gold-divider" /> {eyebrow}
    </p>
    <h2 className="font-display text-2xl md:text-3xl mt-3 mb-6">{title}</h2>
    {children}
  </section>
);

const Field = ({ label, value, onChange, placeholder, type = "text", testid, hint }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">{label}</span>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="input-luxe mt-2 w-full px-4 py-3 text-sm"
    />
    {hint && <p className="mt-1 text-[10px] tracking-wider text-white/40">{hint}</p>}
  </label>
);

const TextareaField = ({ label, value, onChange, placeholder, testid, rows = 3, hint }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">{label}</span>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      className="input-luxe mt-2 w-full px-4 py-3 text-sm resize-y leading-relaxed"
    />
    {hint && <p className="mt-1 text-[10px] tracking-wider text-white/40">{hint}</p>}
  </label>
);

const AdminSettingsPage = () => {
  const { refresh } = useSiteContent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await fetchSiteSettings();
      setData(s);
    } catch {
      toast.error("Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const update = (path, key, value) => {
    setData((prev) => ({
      ...prev,
      [path]: { ...(prev?.[path] || {}), [key]: value },
    }));
  };

  // Auto-derive whatsappDigits whenever the WhatsApp number changes
  const updateWhatsApp = (val) => {
    const digits = (val || "").replace(/\D/g, "");
    setData((prev) => ({
      ...prev,
      contact: { ...(prev?.contact || {}), whatsapp: val, whatsappDigits: digits },
    }));
  };

  const onSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await updateSiteSettings({
        contact: data.contact,
        social: data.social,
        brand: data.brand,
        seo: data.seo,
        brochure: data.brochure,
      });
      await refresh();
      toast.success("Settings saved.", { description: "Public site will reflect changes immediately." });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Save failed.", { description: d || "" });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout eyebrow="Settings" title="Site Settings">
        <div className="flex items-center gap-3 text-white/65">
          <Loader2 className="h-4 w-4 animate-spin text-[color:var(--sp-gold)]" /> Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      eyebrow="Settings"
      title="Site Settings"
      description="Manage contact information, brand text, and social media links. Used across the navbar, footer, contact page, and SEO tags."
      actions={
        <>
          <button onClick={load} className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-settings-save"
            className="btn-gold neon-button inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? "Saving" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="space-y-8">
        <Section eyebrow="Brand" title="Brand & Tagline">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Brand Name" value={data.brand?.name} onChange={(v) => update("brand", "name", v)} testid="settings-brand-name" />
            <Field label="Legal Name" value={data.brand?.legalName} onChange={(v) => update("brand", "legalName", v)} testid="settings-brand-legal" />
            <Field label="Tagline" value={data.brand?.tagline} onChange={(v) => update("brand", "tagline", v)} testid="settings-brand-tagline" />
            <Field label="Founded Year (EST.)" value={data.brand?.foundedYear} onChange={(v) => update("brand", "foundedYear", v)} testid="settings-brand-year" />
          </div>
        </Section>

        <Section eyebrow="Contact" title="Contact Information">
          <div className="grid md:grid-cols-2 gap-5">
            <Field
              label="Phone / WhatsApp"
              value={data.contact?.whatsapp}
              onChange={updateWhatsApp}
              placeholder="+91 96372 41414"
              testid="settings-contact-whatsapp"
            />
            <Field
              label="WhatsApp Digits (auto)"
              value={data.contact?.whatsappDigits}
              onChange={(v) => update("contact", "whatsappDigits", v)}
              placeholder="919637241414"
              testid="settings-contact-digits"
            />
            <Field label="Email" value={data.contact?.email} onChange={(v) => update("contact", "email", v)} type="email" testid="settings-contact-email" />
            <Field label="Website" value={data.contact?.website} onChange={(v) => update("contact", "website", v)} testid="settings-contact-website" />
            <Field label="Headquarters" value={data.contact?.headquarters} onChange={(v) => update("contact", "headquarters", v)} testid="settings-contact-hq" />
            <Field label="Office Hours" value={data.contact?.officeHours} onChange={(v) => update("contact", "officeHours", v)} testid="settings-contact-hours" />
            <div className="md:col-span-2">
              <Field label="Site Address (Silva City)" value={data.contact?.siteAddress} onChange={(v) => update("contact", "siteAddress", v)} testid="settings-contact-site" />
            </div>
          </div>
        </Section>

        <Section eyebrow="Social" title="Social Media Links">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="LinkedIn" value={data.social?.linkedin} onChange={(v) => update("social", "linkedin", v)} testid="settings-social-linkedin" />
            <Field label="Instagram" value={data.social?.instagram} onChange={(v) => update("social", "instagram", v)} testid="settings-social-instagram" />
            <Field label="Facebook" value={data.social?.facebook} onChange={(v) => update("social", "facebook", v)} testid="settings-social-facebook" />
            <Field label="YouTube" value={data.social?.youtube} onChange={(v) => update("social", "youtube", v)} testid="settings-social-youtube" />
            <Field label="X / Twitter" value={data.social?.twitter} onChange={(v) => update("social", "twitter", v)} testid="settings-social-twitter" />
          </div>
        </Section>

        <Section eyebrow="SEO" title="Search Engine Optimization">
          <p className="text-xs text-white/55 mb-5 leading-relaxed">
            These defaults are used as fallbacks across every public page (title template, description, social-share image, etc.).
            Per-page titles (Home, About, Projects, Services, Contact) are auto-applied on top of these.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <Field
              label="Canonical Site URL"
              value={data.seo?.siteUrl}
              onChange={(v) => update("seo", "siteUrl", v)}
              placeholder="https://www.seetaiproperties.com"
              testid="settings-seo-site-url"
              hint="Used for canonical tags + Open Graph URLs. Include https:// — no trailing slash."
            />
            <Field
              label="Site Title (homepage / fallback)"
              value={data.seo?.siteTitle}
              onChange={(v) => update("seo", "siteTitle", v)}
              placeholder="Seetai Properties — Crafting Luxury. Building Legacy."
              testid="settings-seo-site-title"
            />
            <Field
              label="Title Template"
              value={data.seo?.titleTemplate}
              onChange={(v) => update("seo", "titleTemplate", v)}
              placeholder="%s | Seetai Properties"
              testid="settings-seo-title-template"
              hint="%s is replaced by each page's own title."
            />
            <Field
              label="Locale"
              value={data.seo?.locale}
              onChange={(v) => update("seo", "locale", v)}
              placeholder="en_IN"
              testid="settings-seo-locale"
              hint="OpenGraph locale, e.g. en_IN, en_US, hi_IN"
            />
            <div className="md:col-span-2">
              <TextareaField
                label="Default Meta Description"
                value={data.seo?.defaultDescription}
                onChange={(v) => update("seo", "defaultDescription", v)}
                placeholder="A one-paragraph summary of Seetai Properties used as a fallback on pages without their own description."
                testid="settings-seo-description"
                rows={3}
                hint="Recommended: 150–160 characters for ideal Google snippet."
              />
            </div>
            <div className="md:col-span-2">
              <TextareaField
                label="Default Keywords"
                value={data.seo?.defaultKeywords}
                onChange={(v) => update("seo", "defaultKeywords", v)}
                placeholder="luxury real estate, premium plots Pune, Silva City, NA plotting, Bhandgaon township"
                testid="settings-seo-keywords"
                rows={2}
                hint="Comma-separated. Used as fallback meta keywords."
              />
            </div>
            <Field
              label="OG / Social Share Image URL"
              value={data.seo?.ogImageUrl}
              onChange={(v) => update("seo", "ogImageUrl", v)}
              placeholder="/og-image.jpg or absolute URL"
              testid="settings-seo-og-image"
              hint="1200×630px recommended. Relative paths resolve against Site URL."
            />
            <Field
              label="Twitter / X Handle"
              value={data.seo?.twitterHandle}
              onChange={(v) => update("seo", "twitterHandle", v)}
              placeholder="@seetaiproperties"
              testid="settings-seo-twitter"
            />
          </div>
        </Section>

        <Section eyebrow="Brochure" title="Brochure Download — Fallback Copy">
          <p className="text-xs text-white/55 mb-5 leading-relaxed">
            Shown to visitors in a popup when they submit the brochure form on the{" "}
            <span className="text-[color:var(--sp-gold)]">Projects</span> page and no
            PDFs have been uploaded yet under{" "}
            <a
              href="/admin/brochures"
              className="text-[color:var(--sp-gold)] hover:underline"
            >
              Admin → Brochures
            </a>
            . The lead is captured either way; this is just the message they see.
          </p>
          <div className="grid md:grid-cols-1 gap-5">
            <Field
              label="Popup Title"
              value={data.brochure?.unavailableTitle}
              onChange={(v) => update("brochure", "unavailableTitle", v)}
              placeholder="We'll send it to you shortly."
              testid="settings-brochure-unavailable-title"
              hint="Headline shown at the top of the popup."
            />
            <TextareaField
              label="Popup Message"
              value={data.brochure?.unavailableText}
              onChange={(v) => update("brochure", "unavailableText", v)}
              placeholder="Thank you for your interest. The brochure isn't available for direct download right now — we'll send it to your inbox shortly, and if you've shared a phone number, our team will reach out via WhatsApp."
              testid="settings-brochure-unavailable-text"
              rows={4}
              hint="Tone and wording are up to you. WhatsApp + Email buttons are added automatically below this message."
            />
          </div>
        </Section>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
